/**
 * The supplier assessment a supplier fills in on the portal's *Your company* page
 * (docs/features/supplier-diligence/04 §3, with Ben's rulings of 3 Nov 2026): which sections its risk tier sends, the
 * questions each section asks, and what is still needed before it can be submitted.
 *
 * suppliers_module records the sections an assessment sends (with the form, the quality questions and whether the
 * supplier is outside the UK) and the portal renders what the server sends; the step list, the vocabularies and
 * `missingItems` stay here so a section ticks as the supplier answers. `missingItems` restates the server's submit rule
 * (`_assessment_missing_items.py`): `modules/suppliers_module/tests/fixtures/assessment_missing_items.json` holds the
 * two to each other. Platform-neutral: strings and pure functions only.
 */

export type RiskTier = "low" | "medium" | "high";
export type YesNo = "" | "yes" | "no";

/** Why each form is sent, for the banner at the top of its first section. The form's name and length come from the server. */
export const FORM_WHY: Record<RiskTier, string> = {
  low: "We ask suppliers of goods and services we use ourselves for this form.",
  medium:
    "We ask suppliers of work that becomes part of what we sell for this form.",
  high: "We ask suppliers of work that handles personal data for us or our customers, or that we could not quickly move elsewhere, for this form.",
};

/** Why an assessment was sent: Pollards' first request, a review, or the supplier's own update of its answers. */
export type AssessmentReason = "first" | "review" | "update";

export const ASSESSMENT_REASON_LABEL: Record<AssessmentReason, string> = {
  first: "First assessment",
  review: "Pollards’ review",
  update: "Your update",
};

/** What accepting an assessment did: approved the supplier, or accepted an update's changes (R16). */
export type AssessmentOutcome = "approved" | "changes_accepted";

export const ASSESSMENT_OUTCOME_LABEL: Record<AssessmentOutcome, string> = {
  approved: "Approved",
  changes_accepted: "Changes accepted",
};

export type SectionId =
  | "company"
  | "products"
  | "despatch"
  | "quality_manager"
  | "iso"
  | "quality_questions"
  | "insurance"
  | "slavery"
  | "gdpr"
  | "security"
  | "continuity"
  | "agreement"
  | "review";

/** A section the server sends, worded for the supplier's step list. */
export interface SentSection {
  id: Exclude<SectionId, "review">;
  label: string;
}

/** What the assessment was sent with: the form, the sections it asks and the answers that decide its questions. */
export interface AssessmentRequest {
  tier: RiskTier;
  form: { label: string; minutes: number };
  /** S8: FSC- or PEFC-labelled work will go through the supplier. */
  fsc_needed: boolean;
  /** S1: the supplier will process personal data for us or our customers. */
  personal_data: boolean;
  /** S7: the supplier is based outside the UK, so it gives its EORI number. */
  outside_uk: boolean;
  /** The sections asked, in the order the step list shows them. */
  sections: SentSection[];
  /** The quality question numbers (1 to 16) the form asks; none on the short form. */
  quality_questions: number[];
  /** The person at Pollards who asked; null for the supplier's own update, or someone who has left. */
  requested_by: string | null;
  requested_on: string;
}

/** One step of the supplier's list: a section the assessment asks, or the last step, Check and submit. */
export interface AssessmentStep {
  id: SectionId;
  label: string;
  /** Skipped because the supplier has an ISO 9001 certificate. */
  skipped?: boolean;
}

/** The step list: the sections the server sent, the quality questions struck out once ISO 9001 is answered yes, then
 *  the last step. */
export function stepsFor(
  request: AssessmentRequest,
  isoAccredited: boolean,
): AssessmentStep[] {
  return [
    ...request.sections.map((section): AssessmentStep => ({
      ...section,
      skipped: section.id === "quality_questions" && isoAccredited,
    })),
    { id: "review", label: "Check and submit" },
  ];
}

/** The CRM's sixteen quality management questions, as the supplier reads them. */
export const QUALITY_QUESTIONS: readonly string[] = [
  "Does your organisation have a quality management system which describes the operating procedures and people responsible for all functions affecting the quality of your service or products?",
  "Is the quality management system regularly audited by senior management?",
  "Is there a system for controlling quality-related documents and for prompt removal of obsolete documents from the workplace?",
  "Are sales contracts reviewed before acceptance to ensure they can be met and goods or services provided on time?",
  "Are incoming goods inspected on arrival to ensure they meet the requirements of the contract?",
  "Are prospective suppliers and sub-contractors assessed to ensure that they can meet your organisation’s requirements?",
  "Are production, processing and service functions planned and controlled?",
  "Are clear work instructions and workmanship standards issued to employees so they can carry out tasks satisfactorily?",
  "Are inspections carried out on your products and services during processing and on completion?",
  "Are products identified at all stages, to show what they are and that inspections have taken place?",
  "Are non-conforming products identified, segregated and properly disposed of?",
  "Is there a procedure to investigate non-conformance in products or services and prevent it happening again?",
  "Is there a procedure for monitoring whether your organisation has met customer requirements?",
  "Is there a procedure for making sure all staff have the training they need to carry out their tasks?",
  "Are quality records kept to show that your products and services meet the specified requirements?",
  "Would you let us visit your premises to carry out an on-site assessment?",
];

/**
 * How a GDPR question is answered: `yes_no` is a Yes or No with an explanation; `explanation` is the explanation alone
 * (the questions that ask "what policies").
 */
export type GdprQuestionKind = "yes_no" | "explanation";

/** One of the CRM's nineteen GDPR and data security questions. */
export interface GdprQuestion {
  /** A stable name for the question; the answers are kept under it. */
  id: string;
  label: string;
  /** What the explanation box asks for. */
  detail_label: string;
  kind: GdprQuestionKind;
}

/**
 * The GDPR and data security questions, in the order the CRM's form lists them. The CRM required an answer and an
 * explanation to every one; One asks only the explanation of the four that ask "what policies" (Ben, 4 Nov). A question
 * is a `yes_no` unless it declares otherwise. The same list is `GDPR_QUESTIONS` in `suppliers_module`'s model:
 * `assessment_missing_items.json` holds the two to each other.
 */
const GDPR_QUESTION_DECLARATIONS: readonly (Omit<GdprQuestion, "kind"> & {
  kind?: GdprQuestionKind;
})[] = [
  {
    id: "dpo_appointed",
    label:
      "Data control: do you have an appointed Data Protection Officer, as GDPR Article 37 requires? Please give their name below.",
    detail_label: "Their name",
  },
  {
    id: "dpo_contact",
    label:
      "Data control: if you have a Data Protection Officer, please give details of how they can be contacted.",
    detail_label: "How they can be contacted",
  },
  {
    id: "equipment_access",
    label:
      "Equipment access control: do you deny unauthorised persons access to the data-processing equipment used for processing personal data?",
    detail_label: "Explain how you control this",
  },
  {
    id: "data_outside_the_uk_and_eea",
    label:
      "Data control: can you confirm that no personal data will be sent or stored outside the UK and the European Economic Area (EEA)?",
    detail_label: "Explain how you control this",
  },
  {
    id: "data_media",
    label:
      "Data media control: do you prevent the unauthorised reading, copying, modification or removal of data media?",
    detail_label: "Explain how you control this",
  },
  {
    id: "storage",
    label:
      "Storage control: do you prevent the unauthorised input of personal data, and the unauthorised inspection, modification or deletion of stored personal data?",
    detail_label: "Explain how you control this",
  },
  {
    id: "user_systems",
    label:
      "User control: do you prevent unauthorised persons from using automated data-processing systems through data communication equipment?",
    detail_label: "Explain how you control this",
  },
  {
    id: "confidentiality",
    label:
      "User control: can you ensure that the persons authorised to access personal data have committed to confidentiality, or are under an appropriate statutory obligation of confidentiality?",
    detail_label: "Explain how you control this",
  },
  {
    id: "training",
    label:
      "User control: can you ensure that all persons authorised to access personal data comply with data protection law, and are trained to the right level in their obligations when processing data?",
    detail_label: "Explain how you control this",
  },
  {
    id: "data_access",
    label:
      "Data access control: do you ensure that persons authorised to use an automated data-processing system can only access the personal data their authorisation covers?",
    detail_label: "Explain how you control this",
  },
  {
    id: "communication",
    label:
      "Communication control: do you ensure that it is possible to verify and establish to which individuals personal data have been, or may be, transmitted or made available using data communication equipment?",
    detail_label: "Explain how you control this",
  },
  {
    id: "input",
    label:
      "Input control: do you ensure that it is possible to verify and establish afterwards which personal data was put into automated data-processing systems, when, and by whom?",
    detail_label: "Explain how you control this",
  },
  {
    id: "data_subject_rights",
    label:
      "Data control: given the nature of the processing, what technical and organisational policies are in place to respond to requests from data subjects exercising their individual rights?",
    detail_label: "Describe your policies",
    kind: "explanation",
  },
  {
    id: "third_party_processors",
    label:
      "Data control: what policies are in place for engaging third-party processors?",
    detail_label: "Describe your policies",
    kind: "explanation",
  },
  {
    id: "transport",
    label:
      "Transport control: do you prevent the unauthorised reading, copying, modification or deletion of personal data during transfers of that data, or while data media are being transported?",
    detail_label: "Explain how you control this",
  },
  {
    id: "recovery",
    label:
      "Recovery: do you ensure that installed systems can be restored if they are interrupted?",
    detail_label: "Explain how you control this",
  },
  {
    id: "destruction",
    label:
      "Data control: what policies do you have to comply with the data controller’s instruction to destroy the data supplied to you, including printed copies and any other material made from it?",
    detail_label: "Describe your policies",
    kind: "explanation",
  },
  {
    id: "breach",
    label:
      "Data control: what incident response and breach notification policies are in place?",
    detail_label: "Describe your policies",
    kind: "explanation",
  },
  {
    id: "integrity",
    label:
      "Integrity: do you ensure that the system’s functions perform, that faults in those functions are reported (reliability), and that stored personal data cannot be corrupted by a malfunction of the system?",
    detail_label: "Explain how you control this",
  },
];

export const GDPR_QUESTIONS: readonly GdprQuestion[] =
  GDPR_QUESTION_DECLARATIONS.map((question) => ({
    kind: "yes_no",
    ...question,
  }));

/**
 * What we buy to sell on, in the dictionary's words (Ben, 3 Nov, option B): its product families and its services,
 * grouped for a supplier to read. Ticks become candidate supplier capabilities when the ISO manager accepts the
 * assessment. The codes are dictionary_module's `FamilyCode` and service kinds.
 */
export const WHAT_WE_SELL: readonly {
  group: string;
  items: readonly [code: string, label: string][];
}[] = [
  {
    group: "Printed products",
    items: [
      ["leaflet", "Leaflets"],
      ["booklet", "Booklets"],
      ["book", "Books"],
      ["stationery", "Stationery"],
      ["card", "Cards"],
      ["ncr", "NCR sets"],
      ["pad", "Pads"],
      ["folder", "Folders"],
      ["label", "Labels"],
      ["envelope", "Envelopes"],
      ["calendar", "Calendars"],
      ["binder", "Binders"],
      ["packaging", "Packaging (printed bags, boxes and cartons)"],
    ],
  },
  {
    group: "Wide format",
    items: [
      ["poster", "Posters"],
      ["banner", "Banners"],
      ["rigid_sign", "Rigid signs"],
      ["vinyl_graphic", "Vinyl graphics"],
      ["canvas", "Canvas prints"],
    ],
  },
  {
    group: "Displays and exhibitions",
    items: [
      ["banner_stand", "Banner stands"],
      ["flag", "Flags"],
      ["lightbox", "Lightboxes"],
      ["exhibition_stand", "Exhibition stands"],
      ["backwall", "Backwalls"],
      ["fabric_backwall", "Fabric backwalls"],
      ["counter", "Counters"],
      ["event_structure", "Event structures"],
      ["table_cover", "Table covers"],
      ["barrier", "Barriers"],
      ["pavement_sign", "Pavement signs"],
    ],
  },
  {
    group: "Goods bought in",
    items: [
      [
        "merchandise",
        "Merchandise (branded clothing, bags, mugs, pens and so on)",
      ],
      [
        "ancillaries",
        "Ancillaries (holders, frames, stands, wallets and other goods sold alongside print)",
      ],
    ],
  },
  {
    group: "Services",
    items: [
      ["mailing", "Mailing"],
      ["fulfilment", "Fulfilment"],
      ["storage", "Storage"],
      ["carriage", "Delivery"],
      ["postage", "Postage"],
      ["installation", "Installation"],
      ["decoration", "Decoration (printing, embroidering or engraving goods)"],
      ["design", "Design"],
    ],
  },
];

/** What we buy for ourselves: not in the dictionary, which describes what we sell. */
export const WHAT_WE_USE: readonly [code: string, label: string][] = [
  ["paper", "Paper and board"],
  [
    "press_consumables",
    "Press consumables (ink, plates, blankets, fount solution)",
  ],
  [
    "finishing_consumables",
    "Finishing consumables (laminate film, wire, glue, blades)",
  ],
  ["packing_materials", "Packing materials (cartons, tape, pallet wrap)"],
  ["prepress_supplies", "Pre-press and proofing supplies"],
  ["engineering", "Engineering and machine servicing"],
  ["it", "IT hardware, software and services"],
  ["office", "Office supplies and furniture"],
];

/** What we want to reach the person receiving the goods (Ben, 3 Nov). */
export const FIELD_CARRIES: readonly [code: string, label: string][] = [
  ["customer_po", "Our customer’s order or PO number"],
  ["instructions", "Delivery instructions"],
  ["contact", "A delivery contact and phone number"],
  ["from", "Who the delivery is from"],
  ["anything", "Anything we choose to put in it"],
  ["other", "Something else"],
];

export const FIELD_SHOWS: readonly [code: string, label: string][] = [
  ["delivery_note", "Delivery note"],
  ["parcel_label", "Parcel label"],
  ["courier", "Given to the courier"],
];

export const UNBRANDED_OPTIONS: readonly [code: string, label: string][] = [
  ["all", "Yes, including courier labels"],
  ["not_courier_labels", "Yes, except for courier labels"],
  ["no", "No"],
];

export const TRACKING_OPTIONS: readonly [code: string, label: string][] = [
  ["yes", "Yes"],
  ["sometimes", "Sometimes"],
  ["no", "No"],
];

export const SECURITY_OPTIONS = [
  "Cyber Essentials",
  "Cyber Essentials Plus",
  "ISO 27001",
  "None of these",
] as const;

export const RESUME_OPTIONS = [
  "Within 2 days",
  "Within a week",
  "Longer than a week",
] as const;

/** One field on the supplier's order form, website or system that we can fill in for the recipient. */
export interface DeliveryField {
  name: string;
  carries: string[];
  other: string;
  shows: string[];
  max_characters: string;
}

export interface Accreditation {
  body: string;
  standard: string;
  expires: string;
}

/** A file the supplier uploaded and an answer names: the kept file, and (for a certificate) when it expires. */
export interface UploadedFile {
  file_id: string;
  name: string;
  size_bytes: number;
}

export interface AttachedFileValue extends UploadedFile {
  /** A certificate's expiry, asked beside its upload ("2027-06-30"); null when not given. */
  expires_on: string | null;
}

export type AttachedFile = AttachedFileValue | null;

/** The answers that hold a file, and the certificates among them (those that expire). */
export type FileAnswer =
  | "iso_9001_certificate"
  | "fsc_certificate"
  | "public_liability"
  | "employers_liability"
  | "slavery_statement"
  | "dpa"
  | "security_certificate";

/** The supplier's answer to one GDPR and data security question, and why. An `explanation` question keeps no `answer`. */
export interface GdprAnswer {
  answer: YesNo;
  comment: string;
}

export interface AssessmentAnswers {
  contact_name: string;
  company_name: string;
  registration_number: string;
  eori_number: string;
  phone: string;
  email: string;
  address: string;
  postcode: string;
  sells: string[];
  uses: string[];
  provides_other: string;
  delivery_fields: DeliveryField[];
  no_delivery_fields: boolean;
  unbranded: string;
  encloses_our_paperwork: YesNo;
  couriers: string[];
  tracking: string;
  has_quality_manager: YesNo;
  quality_manager_name: string;
  quality_manager_phone: string;
  quality_manager_email: string;
  iso_9001: YesNo;
  iso_9001_certificate: AttachedFile;
  accreditations: Accreditation[];
  other_accreditations: string;
  fsc_certificate: AttachedFile;
  quality_answers: Record<number, YesNo>;
  public_liability: AttachedFile;
  employers_liability: AttachedFile;
  over_slavery_threshold: YesNo;
  slavery_statement: AttachedFile;
  ico_complaint: YesNo;
  /** The outcome of the complaint, asked when `ico_complaint` is yes. */
  ico_complaint_outcome: string;
  /** By question id (`GDPR_QUESTIONS`). */
  gdpr_answers: Record<string, GdprAnswer>;
  dpa: AttachedFile;
  security_held: string;
  security_certificate: AttachedFile;
  continuity_plan: YesNo;
  resume_within: string;
  agreed: boolean;
}

export function emptyDeliveryField(): DeliveryField {
  return { name: "", carries: [], other: "", shows: [], max_characters: "" };
}

/** A missing answer, worded for the supplier, with the section it is in. */
export interface MissingItem {
  section: SectionId;
  text: string;
}

/**
 * Everything still needed before the supplier can submit, in section order. A section is complete when none of its
 * items is here, so every question a section asks has an item: the step list ticks a section only when it is complete,
 * never just for being opened (Ben, 3 Nov).
 */
export function missingItems(
  request: AssessmentRequest,
  a: AssessmentAnswers,
): MissingItem[] {
  const sections = new Set<SectionId>(request.sections.map((s) => s.id));
  const missing: MissingItem[] = [];
  const need = (section: SectionId, isMissing: boolean, text: string) => {
    if (sections.has(section) && isMissing) missing.push({ section, text });
  };
  const blank = (...values: string[]) => values.some((v) => !v.trim());
  need(
    "company",
    blank(
      a.contact_name,
      a.company_name,
      a.phone,
      a.email,
      a.address,
      a.postcode,
    ),
    "Give your contact name, company name, telephone, email and address.",
  );
  need(
    "company",
    request.tier !== "low" && blank(a.registration_number),
    "Give your company registration number: we need it for a credit check.",
  );
  need(
    "company",
    request.outside_uk && blank(a.eori_number),
    "Give your EORI number.",
  );
  need(
    "products",
    !a.sells.length && !a.uses.length && !a.provides_other.trim(),
    "Tick at least one thing you provide, or tell us what it is.",
  );
  need(
    "despatch",
    !a.no_delivery_fields &&
      !a.delivery_fields.some((f) => f.name.trim() && f.carries.length),
    "Add a field we can fill in, with what it can carry, or tick “We have no fields like this”.",
  );
  need("despatch", !a.unbranded, "Say whether you can leave your name off.");
  need(
    "despatch",
    !a.encloses_our_paperwork,
    "Say whether you can put our paperwork in the parcel.",
  );
  need(
    "despatch",
    !a.couriers.some((c) => c.trim()),
    "Name at least one courier you use.",
  );
  need(
    "despatch",
    !a.tracking,
    "Say whether you give a tracking number for each parcel.",
  );
  need(
    "quality_manager",
    !a.has_quality_manager,
    "Say whether your organisation has a quality manager.",
  );
  need(
    "quality_manager",
    a.has_quality_manager === "yes" &&
      blank(a.quality_manager_name, a.quality_manager_email),
    "Give your quality manager’s name and email.",
  );
  need(
    "iso",
    !a.iso_9001,
    "Say whether your organisation is accredited to ISO 9001.",
  );
  need(
    "iso",
    a.iso_9001 === "yes" && !a.iso_9001_certificate,
    "Attach your ISO 9001 certificate.",
  );
  need(
    "iso",
    request.fsc_needed && !a.fsc_certificate,
    "Attach your FSC or PEFC certificate.",
  );
  need(
    "quality_questions",
    a.iso_9001 !== "yes" &&
      request.quality_questions.some((n) => !a.quality_answers[n]),
    "Answer every quality question.",
  );
  need(
    "insurance",
    !a.public_liability || !a.employers_liability,
    "Attach your public liability and employer’s liability certificates.",
  );
  need(
    "slavery",
    !a.over_slavery_threshold,
    "Say whether your turnover is over the Modern Slavery Act threshold.",
  );
  need(
    "slavery",
    a.over_slavery_threshold === "yes" && !a.slavery_statement,
    "Attach your latest modern slavery statement.",
  );
  need(
    "gdpr",
    !a.ico_complaint,
    "Say whether a complaint about your organisation has been referred to the ICO.",
  );
  need(
    "gdpr",
    a.ico_complaint === "yes" && blank(a.ico_complaint_outcome),
    "Summarise the outcome of the ICO complaint.",
  );
  // Every question needs its explanation; a yes or no too, except where the question asks "what policies".
  need(
    "gdpr",
    GDPR_QUESTIONS.some(
      (q) => q.kind === "yes_no" && !a.gdpr_answers[q.id]?.answer,
    ),
    "Answer every GDPR and data security question.",
  );
  need(
    "gdpr",
    GDPR_QUESTIONS.some((q) => blank(a.gdpr_answers[q.id]?.comment ?? "")),
    "Explain each GDPR and data security answer.",
  );
  need("gdpr", !a.dpa, "Attach the signed data processing agreement (DPA).");
  need(
    "security",
    !a.security_held,
    "Say which information security certification you hold.",
  );
  need(
    "security",
    Boolean(a.security_held) &&
      a.security_held !== "None of these" &&
      !a.security_certificate,
    `Attach your ${a.security_held} certificate.`,
  );
  need(
    "continuity",
    !a.continuity_plan || !a.resume_within,
    "Answer both business continuity questions.",
  );
  need(
    "agreement",
    !a.agreed,
    "Accept the confidentiality and non-solicitation agreement.",
  );
  return missing;
}
